import React from 'react';
import { Box } from '@mui/material';
import Img from './Img';
import { Maximize2 } from 'lucide-react';
import { colors } from '../theme';

const DOTS = ['#ec6a5e', '#f4bf4f', '#61c554'];

const pillSx = {
    position: 'absolute', right: 14, top: 14,
    display: 'inline-flex', alignItems: 'center', gap: 0.75,
    px: 1.5, py: 0.6, borderRadius: '999px',
    fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.02em',
    color: '#fff', backgroundColor: 'rgba(0,0,0,0.55)',
    backdropFilter: 'blur(8px)',
    transition: 'background-color 0.25s ease',
};

/**
 * A product screenshot inside a device frame: browser chrome for web apps, a
 * thin bezel for the tablet. `onClick` makes it a button; `pill` adds the
 * "N screens" badge so the frame says it opens without a hover; `aspect`
 * locks the image box (cards) so a row of frames lines up.
 */
const ScreenFrame = ({ screen, onClick, label, pill, aspect, compact, eager, sx, className }) => {
    const tablet = screen.frame === 'tablet';
    // A pinned desktop app fills its screen: no browser chrome, no window bar.
    const desktop = screen.frame === 'desktop';
    const chrome = !tablet && !desktop;
    const interactive = Boolean(onClick);
    const bezel = tablet ? (compact ? '6px' : { xs: '8px', md: '14px' }) : 0;
    return (
        <Box
            className={className}
            role={interactive ? 'button' : undefined}
            tabIndex={interactive ? 0 : undefined}
            aria-label={interactive ? label : undefined}
            onClick={onClick}
            onKeyDown={interactive ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); }
            } : undefined}
            sx={{
                position: 'relative',
                ...(aspect && { aspectRatio: aspect, display: 'flex', flexDirection: 'column' }),
                cursor: interactive ? 'pointer' : 'default',
                borderRadius: tablet ? (compact ? '14px' : '20px') : '12px',
                overflow: 'hidden',
                backgroundColor: tablet ? '#1b2128' : '#fff',
                border: tablet ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.09)',
                boxShadow: '0 1px 2px rgba(16,24,20,0.06), 0 16px 40px rgba(16,24,20,0.10)',
                transition: 'transform 0.4s cubic-bezier(0.25,0.1,0.25,1), box-shadow 0.4s ease',
                ...(interactive && {
                    '&:hover, &:focus-visible': {
                        transform: 'translateY(-3px) scale(1.005)',
                        boxShadow: '0 1px 2px rgba(16,24,20,0.06), 0 24px 56px rgba(16,24,20,0.14)',
                        outline: 'none',
                    },
                    '&:hover .frame-pill, &:focus-visible .frame-pill': { backgroundColor: colors.green },
                    '&:focus-visible': { boxShadow: `0 0 0 3px ${colors.green}` },
                }),
                // Camera dot: what makes the bezel read as hardware, not a dark div.
                ...(tablet && !compact && {
                    '&::before': {
                        content: '""', position: 'absolute', top: 5, left: '50%',
                        width: 4, height: 4, borderRadius: '50%', transform: 'translateX(-50%)',
                        backgroundColor: 'rgba(255,255,255,0.18)', zIndex: 1,
                    },
                }),
                ...sx,
            }}
        >
            {chrome && (
                <Box sx={{
                    height: 30, display: 'flex', alignItems: 'center', px: 1.5,
                    backgroundColor: '#f3f5f4', borderBottom: '1px solid rgba(0,0,0,0.07)',
                }}>
                    <Box sx={{ display: 'flex', gap: '7px', flex: '0 0 auto' }}>
                        {DOTS.map((c) => (
                            <Box key={c} sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: c, opacity: 0.85 }} />
                        ))}
                    </Box>
                    <Box sx={{ mx: 'auto', width: '38%', height: 14, borderRadius: '6px', backgroundColor: 'rgba(0,0,0,0.05)' }} />
                    {/* Browser frames carry the pill in the chrome, where a real app
                        keeps nothing; top-right of the screenshot is usually its
                        primary button. */}
                    {pill ? (
                        <Box className="frame-pill" sx={{ ...pillSx, position: 'static', py: 0.25, fontSize: '0.7rem' }}>
                            <Maximize2 size={12} /> {pill}
                        </Box>
                    ) : <Box sx={{ width: 44 }} />}
                </Box>
            )}
            <Box sx={{
                p: bezel,
                ...(aspect && { flex: 1, minHeight: 0, overflow: 'hidden' }),
            }}>
                <Box sx={{
                    position: 'relative', height: aspect ? '100%' : 'auto',
                    borderRadius: tablet ? (compact ? '8px' : '10px') : 0,
                    overflow: 'hidden',
                    ...(tablet && { boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.06)' }),
                }}>
                    <Img
                        src={screen.src}
                        alt={screen.alt}
                        width={screen.w}
                        height={screen.h}
                        loading={eager ? 'eager' : 'lazy'}
                        fetchPriority={eager ? 'high' : undefined}
                        sx={{
                            width: '100%', display: 'block',
                            height: aspect ? '100%' : 'auto',
                            // Tablet screens are 16:9 inside a 16:10 box; cover cut the
                            // stat cards at the edges, so letterbox them in the bezel.
                            objectFit: aspect ? (tablet ? 'contain' : 'cover') : undefined,
                            objectPosition: tablet ? (aspect ? 'center' : 'center top') : 'left top',
                        }}
                    />
                </Box>
            </Box>
            {pill && !chrome && (
                <Box className="frame-pill" sx={{ ...pillSx, display: { xs: 'none', sm: 'inline-flex' } }}>
                    <Maximize2 size={13} /> {pill}
                </Box>
            )}
        </Box>
    );
};

export default ScreenFrame;
